import { DIM_UOM_DESCRIPTIONS, WEIGHT_UOM_DESCRIPTIONS } from '@views/screens/common/constants';
import type { LengthUOMProductId, WeightUOM } from '@views/screens/common/types';
import type { QuoteProductDetails } from '@views/screens/Quote';
import { sum } from 'es-toolkit';
import type { TFunction } from 'i18next';

import { stringToNumber, valueToNumber } from '@/utilz';
import { calculateExtVolumeValue } from '@/utils/measures';

export interface FooterField {
  label: string;
  value: string | number;
}

export interface QuotePayloadProductTotals {
  totalCube: number;
  totalUnits: number;
  totalWeight: number;
}

const WEIGHT_CONVERSIONS: Record<WeightUOM, number> = {
  KG: 2.20462,
  LB: 1,
  OZ: 1 / 16,
};

const CUBIC_METER_TO_CUBIC_FEET = 35.314;

const toNumber = (v: unknown): number => stringToNumber(v) || 0;

const convertWeight = (weight: number, fromUom: WeightUOM = 'LB', toUom: WeightUOM = 'LB'): number => {
  // normalize -> convert to pounds -> then convert to target UOM
  const weightInPounds = weight * WEIGHT_CONVERSIONS[fromUom];
  return weightInPounds / WEIGHT_CONVERSIONS[toUom];
};

const convertCube = (cube: number, fromDim: LengthUOMProductId = 'N', toDim: LengthUOMProductId = 'N'): number => {
  if (fromDim === toDim) {
    return cube;
  }

  // Only convert between cubic meters (C) and cubic feet (N)
  if (fromDim === 'C' && toDim === 'N') {
    return cube * CUBIC_METER_TO_CUBIC_FEET;
  }
  if (fromDim === 'N' && toDim === 'C') {
    return cube / CUBIC_METER_TO_CUBIC_FEET;
  }

  return cube;
};

const getTotalWeight = (items: QuoteProductDetails[], targetUom: WeightUOM = 'LB'): number => {
  return sum(
    items.map((item) => {
      const totalItemWeight = toNumber(item.weight) * toNumber(item.quantity);
      return convertWeight(totalItemWeight, item.weightUom, targetUom);
    }),
  );
};

const getTotalCube = (items: QuoteProductDetails[], targetDim: LengthUOMProductId = 'N'): number => {
  return sum(
    items.map((item) => {
      const totalCube = calculateExtVolumeValue(toNumber(item.quantity), toNumber(item.cube));
      return convertCube(totalCube, item.dimUom, targetDim);
    }),
  );
};

/**
 * Build product footer fields for UI
 */
export const getQuoteProductFooterFields = (t: TFunction, items: QuoteProductDetails[] = []): FooterField[] => {
  if (!items.length) {
    return [];
  }

  const first = items[0];
  const weightUom = first?.weightUomName || (first?.weightUom && WEIGHT_UOM_DESCRIPTIONS[first.weightUom]) || 'LB';
  const dimUom = first?.dimUomName || (first?.dimUom && DIM_UOM_DESCRIPTIONS[first.dimUom]) || 'inches';

  const totalWeight = getTotalWeight(items, first?.weightUom);
  const totalCube = getTotalCube(items, first?.dimUom);
  const totalQuantity = sum(items.map((i) => toNumber(i.quantity)));

  return [
    {
      label: t('translation:getQuote.productDetails.totalWeight'),
      value: Number.isNaN(totalWeight) ? '0' : totalWeight.toLocaleString(),
    },
    {
      label: t('translation:getQuote.productDetails.totalWeightUnitOfMeasure'),
      value: weightUom,
    },
    {
      label: t('translation:getQuote.productDetails.totalQuantity'),
      value: totalQuantity,
    },
    {
      label: t('translation:getQuote.productDetails.totalDimUnitOfMeasure'),
      value: dimUom,
    },
    {
      label: t('translation:getQuote.productDetails.totalCube'),
      value: Number.isNaN(totalCube) ? '0' : totalCube.toLocaleString(),
    },
  ];
};

/**
 * Returns totals for backend payload
 */
export const getQuotePayloadProductTotals = (items: QuoteProductDetails[] = []): QuotePayloadProductTotals => {
  if (!items.length) {
    return { totalCube: 0, totalUnits: 0, totalWeight: 0 };
  }

  const first = items[0];
  const totalWeight = getTotalWeight(items, first?.weightUom);
  const totalCube = getTotalCube(items, first?.dimUom);
  const totalUnits = sum(items.map((i) => toNumber(i.quantity)));

  return {
    totalCube: valueToNumber(totalCube.toFixed(3)),
    totalUnits,
    totalWeight: valueToNumber(totalWeight.toFixed(3)),
  };
};
